import { useEffect, useState } from "react";
import { useGetIdentity, useNotify } from "ra-core";
import { CalendarClock, Check, Copy, ExternalLink } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { TIMEZONE_CHOICES } from "../misc/DateTimeStamp";
import { bookingLinkFor } from "../meetings/bookingApi";
import {
  WEEKDAY_KEYS,
  defaultBookingPage,
  loadBookingPage,
  saveBookingPage,
  validateBookingPage,
  type BookingPageSettings,
  type WeekdayKey,
} from "../meetings/bookingPageSettings";
import { useConfigurationContext } from "../root/ConfigurationContext";

/**
 * Settings → My profile - your meeting scheduler: a public page at
 * <workspace>.obviouscrm.com/meeting/<handle> where people pick a time
 * that fits your working hours (and, once your calendar is connected,
 * isn't already busy). Booked meetings land on the contact and email both
 * of you a calendar invite.
 */

const DURATIONS = [15, 20, 30, 45, 60, 90];
const DAY_LABELS: Record<WeekdayKey, string> = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

export const MeetingSchedulerCard = () => {
  const { identity } = useGetIdentity();
  const { timezone: workspaceTimeZone } = useConfigurationContext();
  const notify = useNotify();

  const [settings, setSettings] = useState<BookingPageSettings | null>(null);
  const [savedHandle, setSavedHandle] = useState<string | null>(null);
  const [savedEnabled, setSavedEnabled] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (identity?.id == null) return;
    loadBookingPage(identity.id)
      .then((row) => {
        setSettings(
          row ?? defaultBookingPage(identity.fullName ?? "", workspaceTimeZone),
        );
        setSavedHandle(row?.handle ?? null);
        setSavedEnabled(row?.enabled ?? false);
      })
      .catch((e: unknown) =>
        setLoadError(e instanceof Error ? e.message : String(e)),
      );
  }, [identity?.id, identity?.fullName, workspaceTimeZone]);

  if (loadError) {
    return (
      <Card>
        <CardContent>
          <Heading />
          <p className="text-sm text-muted-foreground">
            The meeting scheduler isn't available in this workspace yet.
          </p>
        </CardContent>
      </Card>
    );
  }
  if (!settings || identity?.id == null) return null;

  const update = (patch: Partial<BookingPageSettings>) => {
    setProblems([]);
    setSettings({ ...settings, ...patch });
  };
  const setDay = (
    day: WeekdayKey,
    windows: BookingPageSettings["weekly_hours"][WeekdayKey],
  ) => update({ weekly_hours: { ...settings.weekly_hours, [day]: windows } });

  const save = async () => {
    const found = validateBookingPage(settings);
    setProblems(found);
    if (found.length) return;
    setSaving(true);
    try {
      await saveBookingPage(identity.id, settings);
      setSavedHandle(settings.handle);
      setSavedEnabled(settings.enabled);
      notify("Meeting scheduler saved", { type: "success" });
    } catch (e) {
      setProblems([e instanceof Error ? e.message : String(e)]);
    } finally {
      setSaving(false);
    }
  };

  const liveLink =
    savedEnabled && savedHandle ? bookingLinkFor(savedHandle) : null;
  const copy = () => {
    if (!liveLink) return;
    void navigator.clipboard.writeText(liveLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const zones = TIMEZONE_CHOICES.some((z) => z.id === settings.timezone)
    ? TIMEZONE_CHOICES
    : [{ id: settings.timezone, name: settings.timezone }, ...TIMEZONE_CHOICES];

  return (
    <Card>
      <CardContent className="space-y-5">
        <div className="flex items-start justify-between gap-4">
          <Heading />
          <div className="flex items-center gap-2 pt-1">
            <Label htmlFor="booking-enabled" className="text-sm">
              {settings.enabled ? "On" : "Off"}
            </Label>
            <Switch
              id="booking-enabled"
              checked={settings.enabled}
              onCheckedChange={(enabled) => update({ enabled })}
            />
          </div>
        </div>

        {liveLink ? (
          <div className="flex items-center gap-2 rounded-md border px-3 py-2">
            <span className="text-sm truncate flex-1">{liveLink}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={copy}
              aria-label="Copy booking link"
            >
              {copied ? (
                <Check className="w-4 h-4" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
            <Button type="button" variant="ghost" size="sm" asChild>
              <a
                href={liveLink}
                target="_blank"
                rel="noreferrer"
                aria-label="Open booking page"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Turn it on and save to get a link people can use to book time with
            you.
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="booking-handle">Link name</Label>
            <div className="flex items-center gap-1">
              <span className="text-sm text-muted-foreground shrink-0">
                …/meeting/
              </span>
              <Input
                id="booking-handle"
                value={settings.handle}
                maxLength={40}
                onChange={(e) =>
                  update({ handle: e.target.value.toLowerCase() })
                }
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="booking-title">Meeting title</Label>
            <Input
              id="booking-title"
              value={settings.title}
              maxLength={80}
              onChange={(e) => update({ title: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Length</Label>
            <Select
              value={String(settings.duration_minutes)}
              onValueChange={(v) => update({ duration_minutes: Number(v) })}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(DURATIONS.includes(settings.duration_minutes)
                  ? DURATIONS
                  : [settings.duration_minutes, ...DURATIONS]
                ).map((d) => (
                  <SelectItem key={d} value={String(d)}>
                    {d} minutes
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Your time zone</Label>
            <Select
              value={settings.timezone}
              onValueChange={(timezone) => update({ timezone })}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {zones.map((z) => (
                  <SelectItem key={z.id} value={z.id}>
                    {z.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Working hours</Label>
          {WEEKDAY_KEYS.map((day) => {
            const window = settings.weekly_hours[day][0];
            return (
              <div key={day} className="flex items-center gap-3">
                <Checkbox
                  id={`booking-day-${day}`}
                  checked={!!window}
                  onCheckedChange={(on) =>
                    setDay(day, on ? [{ start: "09:00", end: "17:00" }] : [])
                  }
                />
                <Label
                  htmlFor={`booking-day-${day}`}
                  className="w-10 font-normal"
                >
                  {DAY_LABELS[day]}
                </Label>
                {window ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      aria-label={`${DAY_LABELS[day]} start`}
                      className="w-32"
                      value={window.start}
                      onChange={(e) =>
                        setDay(day, [{ ...window, start: e.target.value }])
                      }
                    />
                    <span className="text-muted-foreground">to</span>
                    <Input
                      type="time"
                      aria-label={`${DAY_LABELS[day]} end`}
                      className="w-32"
                      value={window.end}
                      onChange={(e) =>
                        setDay(day, [{ ...window, end: e.target.value }])
                      }
                    />
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    Unavailable
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="booking-notice">Minimum notice (hours)</Label>
            <Input
              id="booking-notice"
              type="number"
              min={0}
              max={720}
              value={settings.min_notice_hours}
              onChange={(e) =>
                update({ min_notice_hours: Number(e.target.value) })
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="booking-ahead">Bookable up to (days ahead)</Label>
            <Input
              id="booking-ahead"
              type="number"
              min={1}
              max={365}
              value={settings.max_days_ahead}
              onChange={(e) =>
                update({ max_days_ahead: Number(e.target.value) })
              }
            />
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          Times already booked through this page are never offered twice.
          Connect your calendar below and anything busy on it is left out too.
        </p>

        {problems.length > 0 && (
          <ul className="text-sm text-destructive list-disc pl-5">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}

        <div className="flex justify-end">
          <Button type="button" onClick={save} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const Heading = () => (
  <div>
    <h2 className="text-xl font-semibold text-muted-foreground flex items-center gap-2">
      <CalendarClock className="w-5 h-5" /> Meeting scheduler
    </h2>
    <p className="text-sm text-muted-foreground mt-1">
      A booking page people can use to pick a time with you.
    </p>
  </div>
);
